/*
  Bosko glide: free, momentum-based scrolling for horizontal carousels (inspired by Embla's dragFree mode).

  - Touch: the browser's own momentum scrolling, with card snapping switched off (see .bm-glide in bosko-modern.css).
  - Mouse: drag with momentum on release, plus a soft rubber-band stretch at either end.
  - Respects prefers-reduced-motion (no momentum or stretch), keeps keyboard and arrow buttons as they are,
    and swallows the click that ends a drag so products don't open by accident.

  Opt a carousel in by adding an entry to GLIDE_TARGETS: `host` is the element that holds the scrolling
  strip, `scroller` finds the strip inside it.
*/
(() => {
  const GLIDE_TARGETS = [
    // Theme carousels: product lists, collection lists, blog posts and product recommendations
    { host: 'slideshow-component.resource-list__carousel', scroller: 'slideshow-slides' },
    // Homepage "In action" videos (its own drag code steps aside for .bm-glide strips)
    { host: '.video-carousel-logos', scroller: '.video-carousel-logos__track' },
    // Homepage hazard section chemical cards: a swipe row below 990px, a fixed grid above it
    { host: '.haz2', scroller: '.haz2__track' },
  ];
  const HOST_SELECTOR = GLIDE_TARGETS.map((target) => target.host).join(', ');

  const FRICTION = 0.95; // velocity kept per 60fps frame while gliding
  const MIN_VELOCITY = 0.02; // px/ms below which the glide stops
  const VELOCITY_WINDOW = 100; // ms of pointer history used to measure the release speed
  const DRAG_THRESHOLD = 5; // px before a press counts as a drag (so plain clicks still work)
  const MAX_STRETCH = 80; // px the strip can be pulled past either end
  const STRETCH_RESISTANCE = 0.35; // how much of the overshoot shows as stretch
  const BOUNCE_FACTOR = 60; // ms of edge-hit velocity converted into bounce distance

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  /** @type {WeakMap<Element, { scroller: HTMLElement }>} one entry per enhanced host */
  const hosts = new WeakMap();

  /** @param {HTMLElement} scroller */
  const maxScroll = (scroller) => scroller.scrollWidth - scroller.clientWidth;

  /**
   * Jump straight to a position: the theme gives the strip scroll-behavior: smooth (for its arrow buttons),
   * which would otherwise turn every per-frame update into a slow animation.
   * @param {HTMLElement} scroller @param {number} left
   */
  const jumpTo = (scroller, left) => scroller.scrollTo({ left, behavior: 'instant' });

  /** @param {HTMLElement} scroller @param {number} px */
  const setStretch = (scroller, px) => scroller.style.setProperty('--bm-glide-stretch', `${px}px`);

  /**
   * The host's own strip. Product cards contain their own small image sliders, so a strip that belongs to
   * a slideshow nested inside the host is skipped.
   * @param {Element} host @param {string} selector
   */
  const findScroller = (host, selector) =>
    [...host.querySelectorAll(selector)].find((el) => {
      const owner = el.closest('slideshow-component');
      return !owner || owner === host || !host.contains(owner);
    });

  /**
   * The theme's slideshow runs its own one-card-per-drag handler on mousedown, but skips it while the
   * slideshow is disabled. Mark it disabled only while this mousedown passes through, so the theme's
   * handler steps aside without blocking the event for anything else on the page.
   * @param {Element} host
   */
  const sidestepThemeDrag = (host) => {
    if (host.localName !== 'slideshow-component' || host.getAttribute('disabled') === 'true') return;
    const previous = host.getAttribute('disabled');
    host.setAttribute('disabled', 'true');
    let restored = false;
    const restore = () => {
      if (restored) return;
      restored = true;
      if (previous === null) host.removeAttribute('disabled');
      else host.setAttribute('disabled', previous);
    };
    // the host's bubble phase runs right after the theme's listener on the strip inside it
    host.addEventListener('mousedown', restore, { once: true });
    // fallback if something stops the event before it bubbles back up (a later press then finds restored = true)
    setTimeout(restore, 0);
  };

  /** @param {Element} host @param {HTMLElement} scroller */
  const attachScroller = (host, scroller) => {
    scroller.classList.add('bm-glide');
    const state = hosts.get(host);
    // a touch or wheel scroll cancels any glide in progress
    scroller.addEventListener('touchstart', () => state?.stopGlide(), { passive: true });
    scroller.addEventListener('wheel', () => state?.stopGlide(), { passive: true });
  };

  /** @param {Element} host @param {string} selector */
  const enhance = (host, selector) => {
    const scroller = findScroller(host, selector);
    if (!(scroller instanceof HTMLElement)) return;

    const existing = hosts.get(host);
    if (existing) {
      // the section re-rendered its strip inside a host we already handle: switch to the new strip
      if (existing.scroller !== scroller) {
        existing.stopGlide();
        existing.scroller = scroller;
        attachScroller(host, scroller);
      }
      return;
    }

    let glideFrame = 0;
    const state = {
      scroller,
      stopGlide() {
        cancelAnimationFrame(glideFrame);
        glideFrame = 0;
      },
    };
    hosts.set(host, state);
    attachScroller(host, scroller);

    /** Spring any stretch back to zero (the CSS transition animates it). */
    const release = () => {
      state.scroller.classList.remove('bm-glide--pulling');
      setStretch(state.scroller, 0);
    };

    /** @param {number} velocity px/ms, positive = scrolling forward */
    const glide = (velocity) => {
      const strip = state.scroller;
      /** @type {number | null} */
      let last = null;

      /** @param {number} now */
      const step = (now) => {
        // time from the frame clock only: mixing in performance.now() can give a negative first step
        const dt = last === null ? 16.67 : Math.max(0, Math.min(now - last, 64));
        last = now;
        velocity *= Math.pow(FRICTION, dt / 16.67);

        const next = strip.scrollLeft + velocity * dt;
        const max = maxScroll(strip);

        if (next <= 0 || next >= max) {
          jumpTo(strip, next <= 0 ? 0 : max);
          // turn the leftover speed into a small bounce against the edge
          const bounce = Math.max(-MAX_STRETCH / 2, Math.min(MAX_STRETCH / 2, -velocity * BOUNCE_FACTOR));
          strip.classList.add('bm-glide--pulling');
          setStretch(strip, bounce);
          requestAnimationFrame(release);
          glideFrame = 0;
          return;
        }

        jumpTo(strip, next);
        glideFrame = Math.abs(velocity) > MIN_VELOCITY ? requestAnimationFrame(step) : 0;
      };

      glideFrame = requestAnimationFrame(step);
    };

    /** @param {MouseEvent} event */
    const onMouseDown = (event) => {
      const strip = state.scroller;
      if (event.button !== 0 || !strip.isConnected) return;
      if (!(event.target instanceof Node) || !strip.contains(event.target)) return;
      if (event.target instanceof Element && event.target.closest('button, input, select, textarea, label')) return;
      // nothing to scroll (e.g. the hazard cards' desktop grid): leave the press alone
      if (maxScroll(strip) <= 0) return;

      sidestepThemeDrag(host);
      state.stopGlide();

      const startX = event.clientX;
      const startScroll = strip.scrollLeft;
      /** @type {{ x: number, t: number }[]} */
      let samples = [{ x: startX, t: performance.now() }];
      let dragging = false;
      const controller = new AbortController();
      const { signal } = controller;

      /** @param {MouseEvent} move */
      const onMove = (move) => {
        // released outside the window: no mouseup arrives, but the next move shows no button held
        if ((move.buttons & 1) === 0) {
          onUp(false);
          return;
        }

        const dx = move.clientX - startX;
        if (!dragging) {
          if (Math.abs(dx) < DRAG_THRESHOLD) return;
          dragging = true;
          strip.classList.add('bm-glide--dragging', 'bm-glide--pulling');
        }
        move.preventDefault();

        const now = performance.now();
        samples.push({ x: move.clientX, t: now });
        samples = samples.filter((s) => now - s.t <= VELOCITY_WINDOW);

        const target = startScroll - dx;
        const max = maxScroll(strip);
        const overshoot = target < 0 ? target : target > max ? target - max : 0;
        jumpTo(strip, Math.max(0, Math.min(max, target)));

        const stretch = reducedMotion.matches
          ? 0
          : Math.max(-MAX_STRETCH, Math.min(MAX_STRETCH, -overshoot * STRETCH_RESISTANCE));
        setStretch(strip, stretch);
      };

      /** @param {boolean} [withClick] whether a click will follow (false when released outside the window) */
      const onUp = (withClick = true) => {
        controller.abort();
        if (!dragging) return;

        strip.classList.remove('bm-glide--dragging');
        release();

        if (withClick) {
          // a drag ends with a click on whatever is under the pointer; don't let it open a product.
          // That click fires in the same task as mouseup, so disarm the guard right after in case none follows.
          const clickGuard = new AbortController();
          window.addEventListener(
            'click',
            (click) => {
              click.preventDefault();
              click.stopPropagation();
            },
            { capture: true, once: true, signal: clickGuard.signal }
          );
          setTimeout(() => clickGuard.abort(), 0);
        }

        if (reducedMotion.matches || !withClick) return;
        const first = samples[0];
        const last = samples[samples.length - 1];
        const elapsed = last.t - first.t;
        if (elapsed <= 0 || performance.now() - last.t > VELOCITY_WINDOW) return;
        glide(-(last.x - first.x) / elapsed);
      };

      window.addEventListener('mousemove', onMove, { signal });
      window.addEventListener('mouseup', () => onUp(), { signal });
      window.addEventListener('blur', () => onUp(false), { signal });
    };

    host.addEventListener('mousedown', onMouseDown, { capture: true });
    // stop the browser's native image/link drag ghost from hijacking the gesture
    host.addEventListener('dragstart', (event) => {
      if (event.target instanceof Node && state.scroller.contains(event.target)) event.preventDefault();
    });
    // an arrow-button click cancels any glide in progress
    host.addEventListener('click', (event) => {
      if (event.target instanceof Element && event.target.closest('button')) state.stopGlide();
    });
  };

  const scan = () =>
    GLIDE_TARGETS.forEach(({ host, scroller }) =>
      document.querySelectorAll(host).forEach((el) => enhance(el, scroller))
    );

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan);
  else scan();

  // The theme editor re-renders sections in place, and product recommendations render their carousel
  // after page load. Only those two need a re-scan, so watch them rather than the whole page.
  document.addEventListener('shopify:section:load', scan);
  const watched = new WeakSet();
  const watchRecommendations = () =>
    document.querySelectorAll('product-recommendations').forEach((el) => {
      if (watched.has(el)) return;
      watched.add(el);
      new MutationObserver(() => {
        if (el.querySelector(HOST_SELECTOR)) scan();
      }).observe(el, { childList: true, subtree: true });
    });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchRecommendations);
  else watchRecommendations();
  document.addEventListener('shopify:section:load', watchRecommendations);
})();
